// frontend/src/pages/EmployerForm.jsx
// Updated with contact_status field for Placement Management
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import api from '../../utils/api';

// Default contact statuses if not passed in
const DEFAULT_CONTACT_STATUSES = [
  { value: "new", label: "New", color: "secondary" },
  { value: "contacted", label: "Contacted", color: "info" },
  { value: "no_answer", label: "No Answer", color: "warning" },
  { value: "not_interested", label: "Not Interested", color: "danger" },
  { value: "do_not_contact", label: "Do Not Contact", color: "dark" },
  { value: "interested", label: "Interested", color: "primary" },
  { value: "pending_opening", label: "Pending Opening", color: "info" },
  { value: "has_opening", label: "Has Opening", color: "success" },
  { value: "placement_made", label: "Placement Made", color: "success" },
];

const EmployerForm = ({
  employer,
  onClose,
  onSuccess,
  contactStatuses = DEFAULT_CONTACT_STATUSES,
}) => {
  const { user } = useContext(AuthContext);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    contact_title: "",
    phone: "",
    email: "",
    website: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    industry: "",
    notes: "",
    is_active: true,
    contact_status: "new",
    next_followup_date: "",
  });

  const isEditing = !!employer;

  useEffect(() => {
    loadStates();
    if (employer) {
      setFormData({
        company_name: employer.company_name || "",
        contact_name: employer.contact_name || "",
        contact_title: employer.contact_title || "",
        phone: employer.phone || "",
        email: employer.email || "",
        website: employer.website || "",
        address_line_1: employer.address_line_1 || "",
        address_line_2: employer.address_line_2 || "",
        city: employer.city || "",
        state_id: employer.state_id || "",
        postal_code: employer.postal_code || "",
        industry: employer.industry || "",
        notes: employer.notes || "",
        is_active: employer.is_active !== false,
        contact_status: employer.contact_status || "new",
        next_followup_date: employer.next_followup_date
          ? employer.next_followup_date.split("T")[0]
          : "",
      });
    }
  }, [employer]);

  const loadStates = async () => {
    try {
      const response = await api.get("/states");
      setStates(response.data);
    } catch (err) {
      console.error("Error loading states:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        tenant_id: user?.tenant_id,
        state_id: formData.state_id || null,
        next_followup_date: formData.next_followup_date || null,
      };

      if (isEditing) {
        payload.updated_by = user?.user_id;
        await api.put(`/employers/${employer.employer_id}`, payload);
      } else {
        payload.created_by = user?.user_id;
        await api.post("/employers", payload);
      }

      onSuccess();
    } catch (err) {
      console.error("Error saving employer:", err);
      setError(err.response?.data?.error || "Failed to save employer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid px-2 px-md-4 py-3 py-md-4">
      <div className="card">
        <div className="card-header bg-primary text-white py-2">
          <h5 className="mb-0 h6">
            <i
              className={`bi bi-${isEditing ? "pencil" : "plus-circle"} me-2`}
            ></i>
            {isEditing ? "Edit" : "Add"}<span className="d-none d-sm-inline"> Employer</span>
          </h5>
        </div>
        <div className="card-body p-2 p-md-3">
          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Company Information */}
            <h6 className="border-bottom pb-2 mb-2 mb-md-3 small">Company Information</h6>
            <div className="row g-2 mb-2 mb-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">
                  Company Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small">Industry</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  placeholder="Healthcare, Retail..."
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small">Website</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="www.example.com"
                />
              </div>
            </div>

            {/* Contact Status Section */}
            <h6 className="border-bottom pb-2 mb-2 mb-md-3 small">Contact Status</h6>
            <div className="row g-2 mb-2 mb-md-3">
              <div className="col-6 col-md-4">
                <label className="form-label small">Status</label>
                <select
                  className="form-select form-select-sm"
                  name="contact_status"
                  value={formData.contact_status}
                  onChange={handleChange}
                >
                  {contactStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small">Next Follow-up</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  name="next_followup_date"
                  value={formData.next_followup_date}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label small">Active Status</label>
                <div className="form-check mt-1">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <label className="form-check-label small" htmlFor="is_active">
                    Employer is active
                  </label>
                </div>
              </div>
            </div>

            {/* Contact Person */}
            <h6 className="border-bottom pb-2 mb-2 mb-md-3 small">Contact Person</h6>
            <div className="row g-2 mb-2 mb-md-3">
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Contact Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-4">
                <label className="form-label small">Title</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="contact_title"
                  value={formData.contact_title}
                  onChange={handleChange}
                  placeholder="HR Manager"
                />
              </div>
              <div className="col-6 col-sm-6 col-md-4">
                <label className="form-label small">Phone</label>
                <input
                  type="tel"
                  className="form-control form-control-sm"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="row g-2 mb-2 mb-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">Email</label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Address */}
            <h6 className="border-bottom pb-2 mb-2 mb-md-3 small">Address</h6>
            <div className="row g-2 mb-2 mb-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">Address Line 1</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="address_line_1"
                  value={formData.address_line_1}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small">Address Line 2</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="address_line_2"
                  value={formData.address_line_2}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="row g-2 mb-2 mb-md-3">
              <div className="col-6 col-md-4">
                <label className="form-label small">City</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small">State</label>
                <select
                  className="form-select form-select-sm"
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {states.map((state) => (
                    <option key={state.state_id} value={state.state_id}>
                      {state.state_name} ({state.state_code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small">Postal Code</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Notes */}
            <h6 className="border-bottom pb-2 mb-2 mb-md-3 small">Notes</h6>
            <div className="mb-2 mb-md-3">
              <textarea
                className="form-control form-control-sm"
                name="notes"
                rows="2"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
              ></textarea>
            </div>

            {/* Form Actions */}
            <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {isEditing ? "Update" : "Create"}<span className="d-none d-sm-inline"> Employer</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployerForm;